import { jsPDF } from "jspdf";

import type { TransactionExportBundle } from "./fetchTransactionExportBundle";
import { getExportMatrices } from "./exportCsv";

type ExportType = TransactionExportBundle["meta"]["type"];

const MARGIN_X = 40;
const MARGIN_TOP = 44;
const MARGIN_BOTTOM = 36;
const ACCENT: [number, number, number] = [11, 132, 203];
const ACCENT_DARK: [number, number, number] = [8, 72, 108];
const INK: [number, number, number] = [28, 37, 44];
const INK_MUTED: [number, number, number] = [92, 107, 120];
const BORDER: [number, number, number] = [210, 218, 226];
const ROW_ALT: [number, number, number] = [245, 248, 251];
const HEADER_TEXT: [number, number, number] = [255, 255, 255];
const MAX_CELL_CHARS = 120;

const TYPE_LABEL: Record<ExportType, string> = {
  orders: "Orders",
  payouts: "Payouts",
  giftcard: "Gift cards",
  billpayment: "Bill payments",
  otc: "Deex Desk (OTC)",
  spread: "Fees (spread)",
  businesses: "Merchants",
};

function trunc(s: string): string {
  const t = String(s ?? "");
  return t.length > MAX_CELL_CHARS ? `${t.slice(0, MAX_CELL_CHARS - 1)}…` : t;
}

/**
 * jsPDF's built-in Helvetica only covers WinAnsi-ish glyphs. Currency symbols
 * like ₦ (U+20A6) trigger substitute fonts with different metrics, so
 * splitTextToSize under-measures width and text bleeds into the next column.
 * Normalize to ASCII labels so wrapping and rendering stay consistent.
 */
function normalizeTextForStandardPdf(s: string): string {
  return String(s ?? "")
    .normalize("NFKC")
    .replace(/\u00A0|\u202F|\u2007|\u2009|\u2060/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\u20A6/g, "NGN ")
    .replace(/€/g, "EUR ")
    .replace(/£/g, "GBP ")
    .replace(/\u00A5/g, "JPY ")
    .replace(/\u2192/g, "->")
    .replace(/[ \t]+/g, " ");
}

function pdfCell(s: unknown): string {
  return trunc(normalizeTextForStandardPdf(String(s ?? "")));
}

function pageContentBottom(doc: jsPDF): number {
  return doc.internal.pageSize.getHeight() - MARGIN_BOTTOM;
}

function pageWidth(doc: jsPDF): number {
  return doc.internal.pageSize.getWidth();
}

function addFooters(
  doc: jsPDF,
  generatedLabel: string
): void {
  const w = pageWidth(doc);
  const h = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.35);
    doc.line(MARGIN_X, h - 26, w - MARGIN_X, h - 26);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...INK_MUTED);
    doc.text("Deex Admin - transaction export (confidential)", MARGIN_X, h - 12);
    doc.text(generatedLabel, MARGIN_X, h - 5);
    doc.text(`Page ${p} of ${total}`, w - MARGIN_X, h - 9, { align: "right" });
  }
}

function drawAccentBar(doc: jsPDF, y: number): void {
  const w = pageWidth(doc);
  doc.setFillColor(...ACCENT);
  doc.rect(0, y, w, 3, "F");
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  const bottom = pageContentBottom(doc);
  if (y + needed <= bottom) return y;
  doc.addPage();
  let nextY = MARGIN_TOP;
  drawAccentBar(doc, 0);
  nextY += 14;
  return nextY;
}

function columnWidths(headers: string[], usable: number): number[] {
  const weights = headers.map((h) => {
    const raw = String(h);
    const u = raw.toUpperCase();
    let w = Math.max(10, Math.min(52, raw.length * 2.2 + 8));
    if (u === "NGN" || u.includes("NGN")) w = Math.max(w, 36);
    else if (u === "USD" || u === "USDT" || u.includes("USDT")) w = Math.max(w, 24);
    else if (u.includes("AMOUNT") || u.includes("BALANCE")) w = Math.max(w, 28);
    return w;
  });
  const sum = weights.reduce((a, b) => a + b, 0);
  return weights.map((wt) => (usable * wt) / sum);
}

function drawTransactionsTable(
  doc: jsPDF,
  headers: string[],
  rows: string[][],
  startY: number
): number {
  let y = startY;
  const usableW = pageWidth(doc) - 2 * MARGIN_X;
  const colWs = columnWidths(headers, usableW);
  const padX = 4;
  const lineH = 8;
  const headerFS = 8;
  const bodyFS = 7;

  const paintHeader = (): void => {
    const headerCells = headers.map((h, i) => {
      const lines = doc.splitTextToSize(pdfCell(h), colWs[i] - padX * 2);
      return lines.length ? lines : [""];
    });
    const headerLineCount = Math.max(1, ...headerCells.map((l) => l.length));
    const headerH = padX * 2 + headerLineCount * lineH + 2;

    if (y + headerH > pageContentBottom(doc)) {
      doc.addPage();
      y = MARGIN_TOP;
      drawAccentBar(doc, 0);
      y += 14;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...INK_MUTED);
      doc.text("Transactions (continued)", MARGIN_X, y);
      y += 16;
      doc.setTextColor(...INK);
    }

    doc.setFillColor(...ACCENT_DARK);
    doc.roundedRect(MARGIN_X, y - 1, usableW, headerH, 1.5, 1.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(headerFS);
    doc.setTextColor(...HEADER_TEXT);
    let x = MARGIN_X;
    for (let i = 0; i < headers.length; i++) {
      let yy = y + padX + lineH * 0.85;
      for (const ln of headerCells[i]) {
        doc.text(ln, x + padX, yy);
        yy += lineH;
      }
      x += colWs[i];
    }
    y += headerH + 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(bodyFS);
    doc.setTextColor(...INK);
  };

  paintHeader();

  for (let r = 0; r < rows.length; r++) {
    const cells = rows[r].map((c) => pdfCell(c));
    const wrapped = cells.map((cell, i) =>
      doc.splitTextToSize(cell, colWs[i] - padX * 2)
    );
    const maxLines = Math.max(1, ...wrapped.map((lines) => lines.length));
    const rowH = padX * 2 + maxLines * lineH;

    if (y + rowH > pageContentBottom(doc)) {
      doc.addPage();
      y = MARGIN_TOP;
      drawAccentBar(doc, 0);
      y += 14;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...INK_MUTED);
      doc.text("Transactions (continued)", MARGIN_X, y);
      y += 16;
      doc.setTextColor(...INK);
      paintHeader();
    }

    const fill = r % 2 === 1 ? ROW_ALT : ([255, 255, 255] as [number, number, number]);
    doc.setFillColor(...fill);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.rect(MARGIN_X, y - 1, usableW, rowH, "FD");

    let x = MARGIN_X;
    for (let i = 0; i < colWs.length; i++) {
      if (i > 0) {
        doc.setDrawColor(...BORDER);
        doc.line(x, y - 1, x, y - 1 + rowH);
      }
      doc.setTextColor(...INK);
      let yy = y + padX + lineH * 0.85;
      for (const ln of wrapped[i]) {
        doc.text(ln, x + padX, yy);
        yy += lineH;
      }
      x += colWs[i];
    }
    y += rowH;
  }

  return y + 8;
}

function drawSummaryBlock(
  doc: jsPDF,
  summary: string[][],
  startY: number
): number {
  let y = startY;
  const w = pageWidth(doc) - 2 * MARGIN_X;
  const colLabel = w * 0.36;
  const colValue = w - colLabel;
  const lineH = 10;
  const rowPad = 5;

  for (const row of summary) {
    const key = pdfCell(row[0]);
    const val = pdfCell(row[1]);
    const keyLines = doc.splitTextToSize(key, colLabel - rowPad * 2);
    const valLines = doc.splitTextToSize(val, colValue - rowPad * 2);
    const lines = Math.max(keyLines.length, valLines.length);
    const rowH = Math.max(lineH, lines * (lineH - 1)) + rowPad;

    y = ensureSpace(doc, y, rowH + 2);
    doc.setDrawColor(...BORDER);
    doc.setFillColor(255, 255, 255);
    doc.setLineWidth(0.25);
    doc.roundedRect(MARGIN_X, y - 1, w, rowH, 1, 1, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...INK);
    doc.text(keyLines, MARGIN_X + rowPad, y + lineH - 1);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...INK_MUTED);
    doc.text(valLines, MARGIN_X + colLabel + rowPad, y + lineH - 1);
    y += rowH + 3;
  }
  return y;
}

export function buildAdminTransactionExportPdfBlob(
  bundle: TransactionExportBundle,
  title: string
): Blob {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const generatedAt = new Date();
  const generatedLabel = `Generated ${generatedAt.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })}`;

  drawAccentBar(doc, 0);
  let y = MARGIN_TOP;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...INK);
  doc.text(pdfCell(title), MARGIN_X, y);
  y += 26;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...INK_MUTED);
  doc.text("Transaction summaries and line items for the selected period.", MARGIN_X, y);
  y += 22;

  doc.setFillColor(...ROW_ALT);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  const metaBoxH =
    52 +
    (bundle.meta.orderSubtype ? 14 : 0) +
    (bundle.meta.billSubtypes ? 14 : 0) +
    (bundle.meta.otcSubtypes ? 14 : 0) +
    (bundle.meta.businessTxType ? 14 : 0) +
    (bundle.meta.businessStatus ? 14 : 0) +
    (bundle.meta.swapStatus ? 14 : 0);
  y = ensureSpace(doc, y, metaBoxH);
  doc.roundedRect(MARGIN_X, y - 4, pageWidth(doc) - 2 * MARGIN_X, metaBoxH, 2, 2, "FD");

  const metaX = MARGIN_X + 14;
  let my = y + 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text("Export type", metaX, my);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...ACCENT_DARK);
  doc.text(TYPE_LABEL[bundle.meta.type], metaX + 72, my);
  my += 14;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...INK);
  doc.text("Date range", metaX, my);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...INK_MUTED);
  doc.text(
    `${bundle.meta.startDate}  ->  ${bundle.meta.endDate}`,
    metaX + 72,
    my
  );
  my += 14;
  if (bundle.meta.orderSubtype) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text("Order subtype", metaX, my);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...INK_MUTED);
    doc.text(pdfCell(bundle.meta.orderSubtype), metaX + 88, my);
    my += 14;
  }
  if (bundle.meta.billSubtypes) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text("Bill subtypes", metaX, my);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...INK_MUTED);
    doc.text(pdfCell(bundle.meta.billSubtypes), metaX + 88, my);
    my += 14;
  }
  if (bundle.meta.otcSubtypes) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text("OTC subtypes", metaX, my);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...INK_MUTED);
    doc.text(pdfCell(bundle.meta.otcSubtypes), metaX + 88, my);
    my += 14;
  }
  if (bundle.meta.businessTxType) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text("Transaction type", metaX, my);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...INK_MUTED);
    doc.text(pdfCell(bundle.meta.businessTxType), metaX + 88, my);
    my += 14;
  }
  if (bundle.meta.businessStatus) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text("Status filter", metaX, my);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...INK_MUTED);
    doc.text(pdfCell(bundle.meta.businessStatus), metaX + 88, my);
    my += 14;
  }
  if (bundle.meta.swapStatus) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text("TRX swap status", metaX, my);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...INK_MUTED);
    doc.text(pdfCell(bundle.meta.swapStatus), metaX + 88, my);
    my += 14;
  }
  y += metaBoxH + 16;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...INK);
  y = ensureSpace(doc, y, 18);
  doc.text("Summary", MARGIN_X, y);
  y += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const { summary: sumRows, headers, rows } = getExportMatrices(bundle);
  y = drawSummaryBlock(doc, sumRows, y);

  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...INK);
  y = ensureSpace(doc, y, 18);
  doc.text("Transactions", MARGIN_X, y);
  y += 18;

  y = drawTransactionsTable(doc, headers, rows, y);

  addFooters(doc, generatedLabel);

  return doc.output("blob");
}
